import { forwardRef, Fragment, useCallback, useEffect, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { isNullish } from '@sas/nova-core/typeguards';
import { Close, ErrorStatus, WarningStatus } from '@sas/react-svg';
import cx from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import { BrowserName, getBrowserName } from '../../../../browser-name';
import { setAlpha } from '../../../../design-tokens';
import { EarconName, useEarcon } from '../../../../hooks/useEarcon';
import { useFeatureFlag } from '../../../../hooks/useFeatureFlag';
import { createUseHtmlId } from '../../../../hooks/useHtmlId';
import { useKeyboardTooltip } from '../../../../hooks/useKeyboardTooltip';
import { useMounted } from '../../../../hooks/useMounted';
import { isTruncated, useTruncationTooltip } from '../../../../hooks/useTruncationTooltip';
import { useIsRTL } from '../../../../locale';
import { createUseTextLocalizer } from '../../../../locale/createUseTextLocalizer';
import { renderDesignTokens, useDesignTokens } from '../../../../theme';
import { escapeRegex } from '../../../../util';
import { markEvent } from '../../../../util/events';
import { setRef } from '../../../../util/ref-util';
import { postfixTestId } from '../../../../util/testId';
import { Button, ButtonVariant } from '../../../Button';
import { useDensity } from '../../../Density';
import { useDirectSpeech } from '../../../DirectSpeech/DirectSpeechProvider';
import { Icon, useIconA11yInfo } from '../../../Icon';
import { InvisibleText } from '../../../InvisibleText';
import { OptionPopper } from '../../../OptionPopper';
import { GlobalSettingsKeys } from '../../../Settings/GlobalSettingsKeys';
import { useGetGlobalSetting } from '../../../Settings/GlobalSettingsProvider';
import { Text } from '../../../Text';
import { MessagePopover } from '../MessagePopover';
import bundle from '../../NovaComponents-Input-gui-icu.properties';
import cn from '../../Input.css.json';
import '../../Input.css';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const useListId = createUseHtmlId('Input-SuggestionList');
const useListItemId = createUseHtmlId('Input-SuggestionListItem');
const useValidationMessageId = createUseHtmlId('Input-ValidationMessage');
const useClearBtnLabelledById = createUseHtmlId('Input-clearButton');
const useDescribedById = createUseHtmlId('Input-describedby');
const useTextLocalizer = createUseTextLocalizer(bundle);
const isCustomMessage = message => message?.message !== undefined;
export const BaseInput = /*#__PURE__*/forwardRef(function BaseInput(props, ref) {
  const suggestionListRef = useRef(null);
  const clearBtnRef = useRef(null);
  const [isInEditState, setEditState] = useState(false);
  const [hasFocus, setHasFocus] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(undefined);
  const [shouldShowSuggestions, setShouldShowSuggestions] = useState(false);
  const [suggestionsFilterValue, setSuggestionsFilterValue] = useState('');
  const localize = useTextLocalizer();
  const isRTL = useIsRTL();
  const refInput = useRef(null);
  const shouldAnnounce = useRef(false); // whether or not to announce number of results for suggestions
  const cursorPositionRef = useRef(-1);
  const isEnterPressedOnIPE = useRef(false);
  const textRef = useRef(null);
  const lastCommittedValueRef = useRef('');
  const suggestions = useMemo(() => {
    if (!shouldShowSuggestions) return [];
    if (props.suggestionItemsFilterFunction) {
      return props.suggestionItemsFilterFunction?.(suggestionsFilterValue, cursorPositionRef.current !== -1 ? cursorPositionRef.current : suggestionsFilterValue.length);
    }
    return props.suggestionItems ? filterSuggestions(props.suggestionItems, suggestionsFilterValue, props.filterType, props.suppressSecondaryTextMatch) : [];
  }, [props, shouldShowSuggestions, suggestionsFilterValue]);
  const showSuggestions = suggestions.length > 0;
  const selectSuggestionHandler = selectedIndex => {
    if (!suggestions || selectedIndex >= suggestions.length || selectedIndex < 0) {
      return;
    }
    const {
      text: value1
    } = suggestions[selectedIndex];
    refInput?.current?.setSelectionRange(cursorPositionRef.current, cursorPositionRef.current + value1?.length - 1, undefined);
    const {
      text: value
    } = suggestions[selectedIndex];
    props.onChange?.({
      value: value || '',
      reason: 'suggestion',
      cursorPosition: refInput?.current?.selectionStart ?? suggestionsFilterValue.length,
      focusedIndex: selectedIndex
    });
    props.onCommit?.({
      value: value || '',
      reason: 'suggestion',
      focusedIndex: selectedIndex
    });
    closeSuggestions();
  };
  const mounted = useMounted();
  const closeSuggestions = useCallback(() => {
    if (mounted.current) {
      setShouldShowSuggestions(false);
      setFocusedIndex(undefined);
      shouldAnnounce.current = false; // prevent announcement of num results after closing suggestions
    }
  }, [mounted]);
  const getTooltip = () => {
    const tooltips = [];
    if (props.keyboardTooltip) {
      tooltips.push(props.keyboardTooltip);
    }
    if (props.value === '' && refInput.current && props.placeholder && isTruncated(refInput.current, props.placeholder)) {
      tooltips.push(props.placeholder);
    }
    if (props.value && refInput.current && isTruncated(refInput.current)) {
      tooltips.push(refInput.current.value);
    }
    if (props.customTooltip) tooltips.push(props.customTooltip);
    return tooltips.join(`\n\n`);
  };

  // https://cheatsheetseries.owasp.org/cheatsheets/DOM_based_XSS_Prevention_Cheat_Sheet.html#rule-6-populate-the-dom-using-safe-javascript-functions-or-properties
  // deepcode ignore DOMXSS: User input is used to create textContent property, which is safe with untrusted data.
  const setTruncationTooltip = useTruncationTooltip({
    tooltip: props.customTooltip
  });
  const type = props.type === 'number' ? 'tel' : props.type;
  const setKeyboardTooltipRef = useKeyboardTooltip({
    getTooltip: () => props.getKeyboardTooltip?.() ?? getTooltip()
  });
  const setRefText = textElement => {
    if (textElement) {
      setRef(ref, textElement);
      textRef.current = textElement;
    }
    if (type !== 'password') {
      setTruncationTooltip(textElement);
      setKeyboardTooltipRef(textElement);
    }
  };
  const setRefInput = useCallback(input => {
    refInput.current = input;
    setRef(ref, input);
    if (type !== 'password') {
      setTruncationTooltip(input);
      setKeyboardTooltipRef(input);
    }
  }, [ref, setKeyboardTooltipRef, setTruncationTooltip, type]);
  useImperativeHandle(props.suggestionsRef, () => ({
    isOpen: () => showSuggestions,
    closeSuggestions: () => closeSuggestions()
  }), [showSuggestions, closeSuggestions]);
  const [shouldHighlight, setShouldHighlight] = useState(false);
  const editable = props.editable ?? true;
  useLayoutEffect(() => {
    !editable && refInput.current?.setSelectionRange(0, 0, undefined);
  }, [editable]);

  // NOTE: this highlights the text in the textbox when the user changes the focus in the OptionPopper.
  //     | The main issue with this approach is we assume the text in the textbox will change before this happens.
  useLayoutEffect(function highlightValue() {
    if (!shouldHighlight) return;
    setShouldHighlight(false);
    // Addding the type check because - setSelectionRange only supports these types
    // for more info, visit - https://developer.mozilla.org/en-US/docs/Web/API/HTMLInputElement
    const inputType = ['text', 'url', 'password', 'tel', 'search', 'week', 'month'];
    // Adding Timeout - many inconsistencies in highlighting for Safari browser
    setTimeout(() => {
      if (!refInput.current || refInput.current !== document.activeElement ||
      // Calling setSelectionRange in Safari would set focus on it, so only proceed if input currently has focus
      type && !inputType.includes(type.toLowerCase())) return;
      if (!props.disabled && refInput.current.value && editable && !props.suggestionItemsFilterFunction || isInEditState) {
        refInput.current.setSelectionRange(0, refInput.current.value.length, undefined);
      } else if (!props.disabled && refInput.current.value && editable && props.suggestionItemsFilterFunction || isInEditState) {
        if (focusedIndex !== undefined && focusedIndex !== null && focusedIndex >= 0) {
          const {
            text: value
          } = suggestions[focusedIndex];
          refInput?.current?.setSelectionRange(cursorPositionRef.current, cursorPositionRef.current + value?.length - 1, undefined);
        }
      } else {
        refInput.current.setSelectionRange(0, 0, undefined);
      }

      // Need to force scroll to occur because safari will cancel scroll if selection occurs.
      refInput.current.scrollIntoView?.({
        block: 'nearest'
      });
    }, 0);
  }, [shouldHighlight, refInput.current?.value, type, props.disabled, isInEditState, editable, props.suggestionItemsFilterFunction, suggestions, focusedIndex, props.variant]);
  const readOnlyOrDisabledField = !editable || props.disabled;
  const refOnChange = useRef(props.onChange);
  useEffect(() => {
    refOnChange.current = props.onChange;
  }, [props.onChange]);

  // Emit an onChange event whenever a new suggestion value is focused
  const currentSuggestion = typeof focusedIndex === 'number' ? suggestions?.[focusedIndex]?.text : undefined;
  useEffect(() => {
    if (isNullish(currentSuggestion)) {
      return;
    }
    // user focused a new suggestion
    refOnChange.current?.({
      value: currentSuggestion,
      reason: 'suggestion',
      cursorPosition: cursorPositionRef.current !== -1 ? cursorPositionRef.current : suggestionsFilterValue.length,
      focusedIndex
    });
    // NOTE: it's probably best to queue this highlight effect after we fire an event.
    setShouldHighlight(true);
  }, [currentSuggestion, suggestionsFilterValue, focusedIndex]);
  const onBlur = event => {
    const relatedToSuggestionList = suggestionListRef.current?.contains(event.relatedTarget) || suggestionListRef.current?.contains(event.target);
    const relatedToClearButton = clearBtnRef.current !== null && (clearBtnRef.current === event.relatedTarget || clearBtnRef.current === event.target);
    window.getSelection && window.getSelection()?.empty();

    /*
      For some reason Firefox doesn't left-align the text once focus moves out of the input field.
      So we need to manually set the selectionRange at the start of the string when the onBlur event is triggered.
    */
    const browserName = getBrowserName();
    browserName === BrowserName.Firefox && refInput.current?.setSelectionRange(0, 0, undefined);
    if (mounted.current && !relatedToSuggestionList && !relatedToClearButton) {
      closeSuggestions();
      mounted.current && setHasFocus(false);
      // TODO: don't need to fire if list item was clicked, we currently fire twice in that case.
      if (focusedIndex !== undefined && focusedIndex !== null && focusedIndex >= 0) props.onCommit?.({
        value: props.value,
        reason: 'suggestion'
      });else props.onCommit?.({
        value: props.value,
        reason: 'blur'
      });
      props.onBlur?.();
      setHasHover(false);
      setHasFocus(false);
      if (props.isInPlaceEditMode) {
        setEditState(!!props.status); // IPE
      }

      shouldAnnounce.current = false;
    }
    if (!readOnlyOrDisabledField && hasFocus && props.revertToLastCommittedValue && refInput.current) {
      lastCommittedValueRef.current = refInput.current.value;
    }
  };
  const handleFocusOnInput = () => {
    if (mounted.current) {
      setHasFocus(true);
      setShouldHighlight(true);
      setShouldShowSuggestions(true);
      if (props.isInPlaceEditMode && !props.disabled && !isEnterPressedOnIPE.current) {
        setEditState(true); // IPE
      }

      props.onFocus?.();
    }
  };
  const onFocus = e => {
    if (clearBtnRef && e.target === clearBtnRef.current) return;
    if (refInput.current && e.target !== refInput.current) {
      refInput.current.focus();
      return;
    }
    handleFocusOnInput();
  };
  const onMouseDown = e => {
    props.onMouseDown?.(e);
  };
  const onMouseUp = e => {
    props.onMouseUp?.(e);
    cursorPositionRef.current = refInput.current?.selectionStart ?? refInput.current?.value.length ?? 0;
  };
  const handleKeyDown = e => {
    const isCtrlOrMetaKey = e.ctrlKey || e.metaKey;
    if (refInput.current && editable && !isCtrlOrMetaKey && (
    // ignore the unprintable characters, and the IME consideration
    e.key.length === 1 || e.keyCode === 229)) {
      if (props.maxLength && props.value.length >= props.maxLength) earcon.play?.();
    }
    const commitOnEnterInDialog = props.commitOnEnterInDialog ?? false;

    // commitOnEnterInDialog keeps the markEvent from happening thus triggering the default action.
    // Marking this event is needed when Input is in a Dialog so that `Escape` doesn't trigger the Dialog's default button

    if (e.key === 'Enter') {
      if (commitOnEnterInDialog) markEvent(e);
      if (!readOnlyOrDisabledField && hasFocus && props.revertToLastCommittedValue && refInput.current) {
        lastCommittedValueRef.current = refInput.current.value;
      }
    }
    const items = (suggestionListRef?.current?.children ? [...suggestionListRef?.current?.children[0].children] : []).filter(i => i.getAttribute('role') !== 'separator');
    const getNonSeparatorItem = index => items[index];
    const scrollIntoView = (index, props) => getNonSeparatorItem(index)?.scrollIntoView?.(props);
    const updateFocusAndScroll = key => {
      setFocusedIndex(prevFocusedIndex => {
        let index;
        if (key === 'ArrowDown') {
          index = !isNullish(prevFocusedIndex) ? Math.min(prevFocusedIndex + 1, suggestions.length - 1) : 0;
        } else if (key === 'ArrowUp') {
          index = !isNullish(prevFocusedIndex) ? Math.max(prevFocusedIndex - 1, 0) : suggestions.length - 1;
        }
        if (index === undefined) return;
        scrollIntoView(index, {
          block: 'nearest'
        });
        return index;
      });
    };
    // readOnlyOrDisabledField keeps the markEvent from happening thus triggering the default action.
    // Marking this event is needed when Input is in a Dialog so that `Escape` doesn't trigger the Dialog's default button
    if (e.key === 'Escape') {
      if (!readOnlyOrDisabledField && hasFocus && props.revertToLastCommittedValue) {
        markEvent(e);
        processChange(lastCommittedValueRef.current.toString());
        // settimeout is necessary for the setselectionrange to function properly
        setTimeout(() => {
          refInput.current?.setSelectionRange(0, lastCommittedValueRef.current.length, undefined);
        }, 0);
        return;
      }
      closeSuggestions();
      const cancelHandled = props.onCancel?.();
      // combobox doesn't highlight text on escape
      if (props.role !== 'combobox') {
        setShouldHighlight(true);
      }
      if (showSuggestions || cancelHandled === true) {
        if (props.role !== 'combobox') {
          e.preventDefault();
          e.stopPropagation();
        }
      }
      return;
    }
    cursorPositionRef.current = refInput.current?.selectionStart ?? refInput.current?.value.length ?? 0;
    if (!showSuggestions || !suggestions || suggestions.length === 0) {
      shouldAnnounce.current = false;
      return;
    }
    if (e.key === 'ArrowDown') {
      // prevent text input cursor from moving
      e.preventDefault();
      mounted.current && updateFocusAndScroll(e.key);
      if (focusedIndex && focusedIndex + 1 === suggestions.length) earcon.play?.();
    } else if (e.key === 'ArrowUp') {
      // prevent text input cursor from moving
      e.preventDefault();
      mounted.current && updateFocusAndScroll(e.key);
      if (focusedIndex === 0) earcon.play?.();
    }
    if (e.key === 'PageDown') {
      // prevent text input cursor from moving
      e.preventDefault();
      e.stopPropagation();
      return;
    } else if (e.key === 'PageUp') {
      // prevent text input cursor from moving
      e.preventDefault();
      e.stopPropagation();
      return;
    }
  };
  const processChange = value => {
    props.onChange?.({
      value,
      reason: 'onChange'
    });
    if (mounted.current) {
      setSuggestionsFilterValue(value);
      setShouldShowSuggestions(true);
      setFocusedIndex(undefined);
      shouldAnnounce.current = true;
    }
  };
  const handleChange = e => {
    cursorPositionRef.current = refInput.current?.selectionStart ?? refInput.current?.value.length ?? 0;
    processChange(e.target.value);
  };
  const handleClear = async () => {
    processChange('');
    refInput.current?.focus();
    handleFocusOnInput();
    refInput.current?.removeAttribute('title');
  };
  const handleKeyPress = event => {
    if (mounted.current && event.key === 'Enter') {
      // user is committing the current value
      // TODO: do we need to reset the selected index too?
      closeSuggestions();
      if (focusedIndex !== undefined && focusedIndex !== null && focusedIndex >= 0) {
        props.onCommit?.({
          value: props.value,
          reason: 'suggestion'
        });
      } else {
        props.onCommit?.({
          value: props.value,
          reason: 'enter'
        });
      }
      if (props.isInPlaceEditMode) {
        setEditState(!!props.status); // IPE
        isEnterPressedOnIPE.current = !props.status;
      }
    }
  };
  useEffect(() => {
    if (!isInEditState && textRef.current && props.isInPlaceEditMode && isEnterPressedOnIPE.current) {
      textRef.current.focus();
      isEnterPressedOnIPE.current = false;
    }
  }, [props.isInPlaceEditMode, isInEditState]);
  const [hasHover, setHasHover] = useState(false);
  const handleMouseOver = () => setHasHover(true);
  const handleMouseLeave = () => setHasHover(false);
  const dt = useDesignTokens();
  const {
    density
  } = useDensity();
  const isCustomFocusTurnedOn = useGetGlobalSetting(GlobalSettingsKeys.FOCUS_OVERRIDE) === 'true';

  // Feature flag for 'New Token Values'
  const isNewTokenValueFlagEnabled = useFeatureFlag('NOVACOMMONS-22288_ThemeModernization_NewTokenValues');
  const tokens = {
    ...getInputDesignTokens(dt, {
      disabled: props.disabled,
      font: props.font,
      theme: props.theme,
      placeholderTheme: props.placeholderTheme,
      status: props.status,
      editable,
      variant: props.variant,
      density,
      isNewTokenValueFlagEnabled,
      isCustomFocusTurnedOn
    }),
    ...props.futureTheme?.(dt)
  };
  const idList = useListId();
  const listItemIds = useListItemId(suggestions?.length || 0);
  const idValidationMessage = useValidationMessageId();
  const tooltipId = useDescribedById();
  const ariaDescribedBy = [];
  if (props.iconProps?.icon && props.value && !(props.iconProps?.tooltip === '' && props.iconProps?.ariaLabel === '')) ariaDescribedBy.push(props.iconProps?.id);
  if (props.customTooltip) ariaDescribedBy.push(tooltipId);
  if (props.ariaDescribedBy) {
    ariaDescribedBy.push(...props.ariaDescribedBy);
  }

  // added for dir attr for these types (NOVACOMMONS-1617)
  const inputType = ['url', 'email', 'tel'];

  // IPE
  const shouldSetFocus = refInput && refInput.current && props.isInPlaceEditMode && isInEditState;
  useEffect(() => {
    if (shouldSetFocus) {
      refInput.current.setSelectionRange(0, 0);
      refInput.current.focus();
    }
  }, [shouldSetFocus]);
  let narratorMessage = '';
  if (suggestions?.length === 0) {
    narratorMessage = localize('narrator.noresults.msg');
  } else if (showSuggestions) {
    if (suggestions?.length === 1) narratorMessage = localize('narrator.result.msg');else {
      narratorMessage = localize('narrator.results.msg', {
        numResults: suggestions?.length.toString()
      });
    }
  }
  let invalidInputMessage = '';
  if (props.status) {
    if (hasFocus && props.message) {
      invalidInputMessage = localize('invalidInput.msg', {
        message: isCustomMessage(props.message) ? props.message.message : props.message
      });
    } else if (props.ariaLabel) {
      if (props.status === 'warning') {
        invalidInputMessage = localize('invalidEntryWarning.aria', {
          label: props.ariaLabel
        });
      } else {
        invalidInputMessage = localize('invalidEntryError.aria', {
          label: props.ariaLabel
        });
      }
    }
  }
  const directSpeech = useDirectSpeech();
  useEffect(() => {
    if (props.status) {
      setEditState(!!props.status);
      directSpeech?.notify(invalidInputMessage);
    }
    if (hasFocus && shouldAnnounce.current && props.value !== '' && props.suggestionItems && suggestions?.length >= 0) {
      directSpeech?.notify(narratorMessage);
      if (showSuggestions) shouldAnnounce.current = false; // don't announce num results while scrolling suggestions list
    }
  }, [props.status, directSpeech, invalidInputMessage, showSuggestions, props.suggestionItems, suggestions?.length, narratorMessage, hasFocus, props.value, shouldAnnounce]);
  const showClearButton = !!props.showClearButton && props.value !== '' && props.required !== true && props.disabled !== true && (editable || !editable && props.variant === 'composite');
  const earcon = useEarcon({
    source: EarconName.InputFull
  });
  const clearBtnLabelledById = useClearBtnLabelledById();
  const {
    clearBtnTooltip,
    clearBtnAriaLabel,
    orgClearBtnAriaLabel,
    clearBtnAriaLabelledBy
  } = useMemo(() => {
    const clearBtnTooltip = props.clearButtonTooltip || localize('clear.tooltip.label');
    const orgClearBtnAriaLabel = props.clearButtonAriaLabel || clearBtnTooltip;
    const clearBtnAriaLabel = props.ariaLabel ? `${orgClearBtnAriaLabel}, ${props.ariaLabel}` : orgClearBtnAriaLabel;
    const clearBtnAriaLabelledBy = props.ariaLabelledBy ? [clearBtnLabelledById, props.ariaLabelledBy].join(' ') : undefined;
    return {
      clearBtnTooltip,
      clearBtnAriaLabel,
      orgClearBtnAriaLabel,
      clearBtnAriaLabelledBy
    };
  }, [localize, props.ariaLabel, props.ariaLabelledBy, props.clearButtonAriaLabel, props.clearButtonTooltip, clearBtnLabelledById]);
  const invisibleText = props.ariaLabelledBy ? /*#__PURE__*/_jsx(InvisibleText, {
    id: clearBtnLabelledById,
    children: orgClearBtnAriaLabel
  }) : null;
  const getIconA11yInfo = useIconA11yInfo();
  const iconTooltip = props.iconProps ? props.iconProps.tooltip ?? getIconA11yInfo(props.iconProps).tooltip : '';
  const iconAriaLabel = props.iconProps?.ariaLabel ?? iconTooltip;
  const shouldPlayAnimation = useGetGlobalSetting(GlobalSettingsKeys.PLAY_VISUAL_EFFECTS) === 'true';
  const iconAnimation = !shouldPlayAnimation ? undefined : {
    closed: {
      opacity: 0
    },
    open: {
      opacity: 1,
      x: 0,
      y: 0,
      transition: {
        type: 'tween',
        duration: 0.3,
        ease: [0, 0.5, 0.2, 1]
      }
    },
    exit: {
      opacity: 0,
      transition: {
        type: 'tween',
        duration: 0.3,
        ease: [0.5, 0, 0.8, 1]
      }
    }
  };
  return /*#__PURE__*/_jsx(Fragment, {
    children: /*#__PURE__*/_jsxs("div", {
      className: cx(cn.container, {
        [cn.hasStatusXorClear]: !!props.status !== showClearButton,
        [cn.hasStatusAndClear]: !!props.status && showClearButton,
        [cn.featureFlagEnabled]: isNewTokenValueFlagEnabled,
        [cn.focused]: hasFocus && !props.disabled && props.variant !== 'composite'
      }, props.className),
      style: {
        ...tokens,
        ...props.style
      },
      onBlur: onBlur,
      onFocus: onFocus,
      onMouseDown: event => {
        onMouseDown(event);
      },
      onMouseUp: event => {
        onMouseUp(event);
      },
      "data-testid": props.testId,
      children: [props.iconProps?.icon && props.value && /*#__PURE__*/_jsx("span", {
        className: cx(cn.compositeInputIcon, props.variant === 'composite' && !editable ? cn.nonEditable : cn.readonly),
        children: /*#__PURE__*/_jsx(Icon, {
          className: cx(cn.compositeInputIcon, props.iconProps.className),
          ...props.iconProps,
          style: {
            '--icon-height': density === 'high' ? dt.tokens['font-size-body-text-s'] : dt.tokens['font-size-body-text-m'],
            '--icon-width': density === 'high' ? dt.tokens['font-size-body-text-s'] : dt.tokens['font-size-body-text-m']
          },
          id: props.iconProps.id,
          tooltip: iconTooltip,
          ariaLabel: iconAriaLabel
        })
      }), props.isInPlaceEditMode && !isInEditState ?
      /*#__PURE__*/
      // IPE
      _jsx(Text, {
        className: cn.inplaceEdit,
        ref: setRefText,
        testId: postfixTestId(props.testId, 'inplaceEdit'),
        disabled: props.disabled,
        focusable: true,
        wrapText: false,
        onClick: () => {
          handleFocusOnInput();
        },
        theme: () => {
          return {
            '--text-font-size-m': 'var(--input-font-size)'
          };
        },
        children: props.value
      }) : /*#__PURE__*/_jsx("input", {
        dir: isRTL && type && inputType.includes(type.toLowerCase()) ? 'ltr' : undefined,
        tabIndex: props.tabindex,
        ...((props.title || props.customTooltip) && {
          title: props.title ?? props.customTooltip
        }),
        className: cx(cn.input, {
          [cn.hasInputIcon]: props.status,
          [cn.hideBorder]: props.hideBorder,
          [cn.disabled]: props.disabled,
          [cn.readonly]: !editable,
          [cn.composite]: props.variant === 'composite',
          [cn.rtl]: isRTL
        }),
        "data-testid": postfixTestId(props.testId, 'input'),
        ref: setRefInput,
        value: props.value,
        placeholder: !editable && (props.variant === undefined || props.variant === 'standard') ? undefined : props.placeholder,
        onChange: handleChange,
        onKeyDown: handleKeyDown,
        onKeyPress: handleKeyPress,
        onMouseEnter: handleMouseOver,
        onMouseLeave: handleMouseLeave,
        onKeyUp: () => {},
        type: type === 'email' ? 'text' : type ?? 'text',
        autoComplete: type === 'password' ? 'new-password' : !!props.enableAutoComplete ? 'on' : 'off'
        // Set aria-autocomplete='list' here if there are suggestions displayed (for now).  This setting should be changed to be aria-autocomplete='both'
        // in the future when the highlighted "type-ahead" functionality is added to the Input (when only one filtered selection exists). This directive
        // is per the recent Accessibility Clinic review of Input (8/10/2021) with Jesse Sookne/Greg Kraus.
        ,
        "aria-autocomplete": showSuggestions ? 'list' : undefined,
        "aria-haspopup": showSuggestions ? true : undefined,
        "aria-required": props.required,
        "aria-disabled": props.disabled,
        "aria-hidden": props.ariaHidden,
        "aria-invalid": props.status === 'alert' || props.ariaInvalid ? true : undefined,
        "aria-describedby": ariaDescribedBy.length > 0 ? ariaDescribedBy.join(' ') : undefined,
        "aria-controls": showSuggestions ? idList : undefined,
        "aria-activedescendant": showSuggestions && !isNullish(focusedIndex) ? listItemIds[focusedIndex] : undefined,
        "aria-label": props.ariaLabel,
        "aria-labelledby": props.ariaLabelledBy,
        id: props.id,
        readOnly: !editable || props.disabled,
        "aria-readonly": !editable,
        maxLength: props.maxLength,
        "data-landmark-label": props.landmarkLabel,
        role: props.role,
        onFocus: e => {
          if (props.disabled || !editable) e.target.setSelectionRange(0, 0, undefined);
        }
      }), props.customTooltip ? /*#__PURE__*/_jsx(InvisibleText, {
        id: tooltipId,
        children: props.customTooltip
      }) : null, props.status ? /*#__PURE__*/_jsx("span", {
        "aria-live": "off",
        role: "status",
        style: {
          width: '0',
          overflow: 'hidden'
        },
        children: /*#__PURE__*/_jsx("span", {
          className: cn.offscreen,
          id: idValidationMessage,
          children: localize('invalidInput.msg', {
            message: isCustomMessage(props.message) ? props.message.message : props.message
          })
        })
      }) : null, showClearButton ? /*#__PURE__*/_jsxs(Fragment, {
        children: [/*#__PURE__*/_jsx(Button, {
          ref: e => {
            {
              clearBtnRef.current = e;
              setRef(props.clearButtonRef, e);
            }
          },
          className: cn.clearButton,
          variant: ButtonVariant.Icon,
          disabled: props.disabled,
          icon: {
            icon: Close
          },
          tooltip: clearBtnTooltip,
          ariaLabel: clearBtnAriaLabel,
          ariaLabelledBy: clearBtnAriaLabelledBy,
          onPress: () => {
            handleClear();
          },
          testId: postfixTestId(props.testId, 'clearButton'),
          tokens: props.clearButtonTokens,
          theme: () => ({
            '--button-hover-bd': 'transparent',
            '--button-active-bd': 'transparent',
            '--button-spacing-min-block-size': 0
          })
        }), invisibleText]
      }) : null, /*#__PURE__*/_jsx(AnimatePresence, {
        children: !hasFocus && props.status ? /*#__PURE__*/_jsx(motion.div, {
          className: cn.inputIcon,
          onMouseEnter: handleMouseOver,
          onMouseLeave: handleMouseLeave,
          variants: iconAnimation,
          initial: "closed",
          animate: "open",
          exit: "exit",
          children: /*#__PURE__*/_jsx(Icon, {
            icon: props.status === 'alert' ? ErrorStatus : WarningStatus,
            disabled: props.disabled,
            testId: postfixTestId(props.testId, 'statusIcon'),
            ...(density === 'high' && {
              style: {
                '--icon-height': dt.tokens['size-icon-s'],
                '--icon-width': dt.tokens['size-icon-s']
              }
            })
          })
        }) : null
      }), /*#__PURE__*/_jsx(OptionPopper, {
        ref: suggestionListRef
        // This will close the suggestion list when the page is scrolled or resized
        ,
        onClose: () => setShouldShowSuggestions(false),
        focusedIndex: focusedIndex,
        listId: idList,
        style: {
          width: props.style?.width ?? refInput?.current?.width,
          overflow: 'auto'
        },
        isOpen: showSuggestions,
        referenceElementRef: refInput,
        hideOnRefHidden: true,
        showSelectedPseudoFocus: true,
        ...(props.enableVirtualScrolling ? {
          virtualItems: {
            count: suggestions.length ?? 0,
            renderItem: ({
              index
            }) => {
              return {
                text: suggestions[index].text,
                secondaryText: suggestions[index].secondaryText,
                concatSecondaryText: suggestions[index].concatSecondaryText,
                icon: suggestions[index].icon,
                iconAriaLabel: suggestions[index].iconAriaLabel,
                iconTooltip: suggestions[index].iconTooltip,
                id: suggestions[index].id,
                indented: suggestions[index].indented,
                tooltip: suggestions[index].tooltip,
                showSeparator: suggestions[index].showSeparator
              };
            },
            width: props.inputContainerWidth ?? refInput.current?.getBoundingClientRect().width
          }
        } : {
          items: suggestions?.map((s, i) => ({
            ...s,
            id: listItemIds[i]
          }))
        }),
        onSelectFocusedItem: () => !isNullish(focusedIndex) && selectSuggestionHandler(focusedIndex),
        onSelected: ({
          index
        }) => selectSuggestionHandler(index),
        listItemFocusable: false,
        testId: postfixTestId(props.testId, 'suggestionPopper'),
        ariaLabel: props.ariaLabelForSuggestionsList ?? localize('suggestedValues.aria'),
        focusLockProps: {
          disabled: true
        }
      }), props.status ? /*#__PURE__*/_jsx(MessagePopover, {
        status: props.status,
        referenceElementRef: refInput,
        ...(isCustomMessage(props.message) ? props.message : {
          message: props.message
        }),
        isOpen: hasFocus || hasHover,
        testId: postfixTestId(props.testId, 'messagePopper'),
        hideOnRefHidden: true
        // Want to blur the input to close the status message for popover's onClose events
        ,
        onClose: () => refInput.current?.blur()
      }) : null]
    })
  });
});

/**
 * Look at `__tests__/InputSuggestionRegex.spec.ts` for usage.
 */
export function createInputSuggestionRegex(value, filterType = 'beginsWith') {
  if (value === '') {
    return null;
  }
  const pattern = escapeRegex(value).replace(/\s/g, '\\s');
  const filteredPattern =
  // deepcode ignore reDOS: user input is sanitized by escapeRegex function.
  filterType === 'contains' ? new RegExp(`${pattern}`, 'i') : new RegExp(`^${pattern}`, 'i');
  return filteredPattern;
}
function filterSuggestions(suggestionItems, value, filterType, suppressSecondaryTextMatch = false) {
  const pattern = createInputSuggestionRegex(value, filterType);
  // we could not create a valid pattern
  if (!pattern) return [];
  return suggestionItems.filter(item => {
    if (suppressSecondaryTextMatch) {
      return item.text && pattern.test(item.text);
    }
    return item.text && pattern.test(item.text) || item.secondaryText && pattern.test(item.secondaryText);
  });
}

/**
 * @deprecated use `getInputDesignTokens` instead
 */
export const getInputThemeTokens = (dt, status) => {
  if (dt.enableNovaTokens) {
    let statusTokens = {};
    if (status) {
      statusTokens = {
        ...(status === 'alert' ? {
          bg: dt.tokens['color-action-background-control-alert'],
          bd: dt.tokens['color-action-border-control-alert']
        } : {}),
        ...(status === 'warning' ? {
          bg: dt.tokens['color-action-background-control-warning'],
          bd: dt.tokens['color-action-border-control-warning']
        } : {}),
        fg: dt.tokens['color-action-text-control-normal']
      };
    } else {
      statusTokens = {
        fg: dt.tokens['color-action-text-control-normal'],
        bg: dt.tokens['color-action-background-control-normal'],
        bd: dt.tokens['color-action-border-bottom-control-normal'],
        'focus-bd': dt.tokens['color-action-border-control-selected'],
        'focus-bg': dt.tokens['color-action-background-control-selected'],
        'readonly-bg': dt.tokens['color-action-background-control-read-only'],
        'readonly-bd': dt.tokens['color-action-border-control-read-only'],
        'readonly-fg': dt.tokens['color-action-text-control-read-only']
      };
    }
    return {
      ...statusTokens,
      'hover-bg': dt.tokens['color-action-background-control-hover'],
      'hover-bd': dt.tokens['color-action-border-bottom-control-hover'],
      'placeholder-fg': dt.tokens['color-action-text-control-placeholder'],
      'selection-bg': dt.tokens['color-action-background-control-primary'],
      'selection-fg': dt.tokens['color-highlight-text-inverse'],
      'disabled-fg': dt.tokens['color-action-text-control-disabled'],
      'disabled-bd': dt.tokens['color-action-border-control-disabled'],
      'disabled-bg': dt.tokens['color-action-background-control-disabled'],
      'noneditable-bg': dt.tokens['color-action-background-control-non-editable'],
      'noneditable-bd': dt.tokens['color-action-border-control-read-only']
    };
  } else {
    // legacy tokens
    if (status) {
      const statusTokens = {
        ...dt.stateStatus[status],
        'selection-bg': dt.reportTokens.text.selection.bg,
        'selection-fg': dt.reportTokens.text.selection.fg
      };
      if (dt.baseReportThemeName === 'highcontrast2017' || dt.baseReportThemeName === 'highcontrast2018') {
        return {
          ...statusTokens,
          'placeholder-fg': dt.c.hcDisabled
        };
      }
      switch (dt.themeVariant) {
        case 'dark':
          return {
            ...statusTokens,
            'placeholder-fg': setAlpha(dt.c.white, 0.3)
          };
        case 'high-contrast':
          return {
            ...statusTokens,
            'placeholder-fg': setAlpha(dt.c.white, 0.5)
          };
        default:
          return {
            ...statusTokens,
            'placeholder-fg': dt.c.n1
          };
      }
    } else {
      if (dt.baseReportThemeName === 'highcontrast2017' || dt.baseReportThemeName === 'highcontrast2018') {
        return {
          bd: dt.c.white,
          bg: dt.c.black,
          fg: dt.c.white,
          'selection-bg': dt.reportTokens.text.selection.bg,
          'selection-fg': dt.reportTokens.text.selection.fg,
          'placeholder-fg': dt.c.hcDisabled,
          'focus-bd': dt.c.p0
        };
      }
      switch (dt.themeVariant) {
        case 'dark':
          return {
            bd: setAlpha(dt.c.white, 0.3),
            bg: dt.c.p_3,
            fg: dt.c.white,
            'selection-bg': dt.reportTokens.text.selection.bg,
            'selection-fg': dt.reportTokens.text.selection.fg,
            'placeholder-fg': setAlpha(dt.c.white, 0.3),
            'focus-bd': dt.c.p0
          };
        case 'high-contrast':
          return {
            bd: dt.c.white,
            bg: dt.c.black,
            fg: dt.c.white,
            'selection-bg': dt.reportTokens.text.selection.bg,
            'selection-fg': dt.reportTokens.text.selection.fg,
            'placeholder-fg': setAlpha(dt.c.white, 0.5),
            'focus-bd': dt.c.p3
          };
        default:
          return {
            bd: dt.c.n1,
            bg: dt.c.white,
            fg: dt.c.n_3,
            'selection-bg': dt.reportTokens.text.selection.bg,
            'selection-fg': dt.reportTokens.text.selection.fg,
            'placeholder-fg': dt.c.n1,
            'focus-bd': dt.c.p0
          };
      }
    }
  }
};

/**
 *
 * @param dt - DesignToken object
 * @param opts - Additional data needed for themeing
 * @returns - An object of CSS Custom Properties used for themeing Input
 */
export const getInputDesignTokens = (dt, opts) => {
  const overrides = opts?.theme;
  const placeholder = opts?.placeholderTheme;
  let overrideValues = {};
  if (overrides) {
    overrideValues = {
      ...renderDesignTokens(overrides({
        state: 'normal'
      }) ?? {}, 'input'),
      ...renderDesignTokens(overrides({
        state: 'alert'
      }) ?? {}, 'input-alert'),
      ...renderDesignTokens(overrides({
        state: 'warning'
      }) ?? {}, 'input-warning')
    };
  }
  if (placeholder) {
    overrideValues = {
      ...overrideValues,
      ...renderDesignTokens(placeholder() ?? {}, 'input-placeholder')
    };
  }
  let border;
  if ((dt.themeVariant === 'high-contrast' || opts?.disabled) && opts?.variant === 'composite') {
    border = 'transparent';
  }
  let statusTokens = {};
  if (opts?.status) {
    statusTokens = {
      ...(opts.status === 'alert' ? {
        bg: dt.tokens['color-action-background-control-alert'],
        bd: dt.tokens['color-action-border-bottom-control-alert'],
        'hover-fg': 'var(--input-fg)',
        'hover-bg': 'var(--input-bg)',
        'hover-bd': 'var(--input-bd)',
        'focus-bd': dt.tokens['color-action-border-bottom-control-alert']
      } : {}),
      ...(opts.status === 'warning' ? {
        bg: dt.tokens['color-action-background-control-warning'],
        bd: dt.tokens['color-action-border-bottom-control-warning'],
        'hover-fg': 'var(--input-fg)',
        'hover-bg': 'var(--input-bg)',
        'hover-bd': 'var(--input-bd)',
        'focus-bd': dt.tokens['color-action-border-bottom-control-warning']
      } : {}),
      fg: dt.tokens['color-action-text-control-normal']
    };
  } else {
    statusTokens = {
      fg: dt.tokens['color-action-text-control-normal'],
      bg: dt.tokens['color-action-background-control-normal'],
      bd: dt.tokens['color-action-border-bottom-control-normal'],
      'hover-fg': dt.tokens['color-action-text-control-hover'],
      'hover-bg': dt.tokens['color-action-background-control-hover'],
      'hover-bd': dt.tokens['color-action-border-bottom-control-hover'],
      'focus-bd': dt.tokens['color-action-border-bottom-control-selected'],
      'focus-bg': dt.tokens['color-action-background-control-selected'],
      'focus-fg': dt.tokens['color-action-text-control-selected'],
      'readonly-bg': dt.tokens['color-action-background-control-read-only'],
      'readonly-bd': dt.tokens['color-action-border-control-read-only'],
      'readonly-fg': dt.tokens['color-action-text-control-read-only']
    };
  }
  const densityTokens = opts?.density === 'high' ? {
    pd: dt.tokens['space-squish-xs'],
    'font-size': dt.tokens['font-size-body-text-s'],
    'min-height': '1.5rem'
  } : {
    pd: dt.tokens['space-squish-s'],
    'font-size': dt.tokens['font-size-body-text-m'],
    'min-height': '1.75rem'
  };
  return {
    ...renderDesignTokens({
      ...statusTokens,
      ...densityTokens,
      'placeholder-fg': dt.tokens['color-action-text-control-placeholder'],
      'selection-bg': dt.tokens['color-highlight-text'],
      'selection-fg': dt.tokens['color-highlight-text-inverse'],
      'disabled-fg': dt.tokens['color-action-text-control-disabled'],
      'disabled-bd': dt.tokens['color-action-border-control-disabled'],
      'disabled-bg': dt.tokens['color-action-background-control-disabled'],
      'noneditable-bg': dt.tokens['color-action-background-control-non-editable'],
      'noneditable-bd': dt.tokens['color-action-border-control-read-only'],
      'min-width': dt.tokens['size-container-xxs'],
      'spacing-xs': dt.tokens['space-base-xs'],
      'line-height': 'normal',
      'inplace-edit-text-fg': dt.tokens['color-action-text-control-normal'],
      ...opts?.font,
      'bd-width': '0 0 1px 0',
      ...(!opts?.isNewTokenValueFlagEnabled && {
        'bd-width': dt.tokens['size-border-width-s']
      }),
      // NOTE: Currently there will be no visible change seen to the component with the below tokens.
      'bottom-bd-bw': dt.tokens['size-border-width-s'],
      'bottom-bd': dt.tokens['color-action-border-bottom-control-normal'],
      'bottom-hover-bd': dt.tokens['color-action-border-bottom-control-hover'],
      'bottom-disabled-bd': dt.tokens['color-action-border-control-disabled'],
      'bottom-warning-bd': dt.tokens['color-action-border-bottom-control-warning'],
      'bottom-alert-bd': dt.tokens['color-action-border-bottom-control-alert'],
      'icon-padding': dt.tokens['space-base-xs'],
      'bottom-bd-block-size': '2px',
      'bottom-bd-anim-dur': dt.tokens['duration-300'],
      'bottom-bd-anim-tm-ease-in': dt.tokens['ease-in'],
      'bottom-bd-anim-tm-ease-out': dt.tokens['ease-out'],
      'focus-outline': 'var(--focus-light-bg)',
      'focus-outline-offset': 'calc(-1 * var(--focus-width))',
      ...(!opts?.isNewTokenValueFlagEnabled && {
        'focus-outline-offset': 'calc(-2px + var(--focus-width) * -1)'
      })
    }, 'input'),
    ...renderDesignTokens({
      'noneditable-bd': border
    }, 'input-focus'),
    ...overrideValues,
    ...(!dt.enableNovaTokens && getInputLegacyDesignTokens(dt, opts))
  };
};

/**
 *
 * @param dt - DesignToken object
 * @param opts - Additional data needed for themeing
 * @returns - An object of CSS Custom Properties used for themeing legacy tokens for Input
 */
export const getInputLegacyDesignTokens = (dt, opts) => {
  const overrides = opts?.theme;
  const placeholder = opts?.placeholderTheme;
  let overrideValues = {};
  if (overrides) {
    overrideValues = {
      ...renderDesignTokens(overrides({
        state: 'normal'
      }) ?? {}, 'input'),
      ...renderDesignTokens(overrides({
        state: 'alert'
      }) ?? {}, 'input-alert'),
      ...renderDesignTokens(overrides({
        state: 'warning'
      }) ?? {}, 'input-warning')
    };
  }
  if (placeholder) {
    overrideValues = {
      ...overrideValues,
      ...renderDesignTokens(placeholder() ?? {}, 'input-placeholder')
    };
  }
  let border;
  if ((dt.themeVariant === 'high-contrast' || opts?.disabled) && opts?.variant === 'composite') {
    if (dt.baseReportThemeName === 'highcontrast2017' || dt.baseReportThemeName === 'highcontrast2018') {
      border = dt.c.p0;
    } else {
      border = dt.c.p3;
    }
  }
  let themeTokens;
  let statusTokens = {};
  if (opts?.status) {
    const tokens = {
      ...dt.stateStatus[opts?.status],
      'readonly-bg': dt.stateStatus[opts?.status].bg,
      'readonly-bd': 'transparent',
      'readonly-fg': dt.stateStatus[opts?.status].fg,
      'noneditable-bg': dt.stateStatus[opts?.status].bg,
      'noneditable-bd': dt.stateStatus[opts?.status].bd,
      'noneditable-fg': dt.stateStatus[opts?.status].fg,
      'disabled-fg': dt.reportTokens.stateStandard.disabled.fg,
      'disabled-bd': dt.reportTokens.stateStandard.disabled.bd,
      'disabled-bg': dt.reportTokens.stateStandard.disabled.bg,
      'selection-bg': dt.reportTokens.text.selection.bg,
      'selection-fg': dt.reportTokens.text.selection.fg,
      'font-size': dt.reportTokens.font.size.m,
      'line-height': 'normal'
    };
    switch (dt.themeVariant) {
      case 'dark':
        statusTokens = {
          ...tokens,
          'placeholder-fg': setAlpha(dt.c.white, 0.3)
        };
        break;
      case 'high-contrast':
        if (dt.baseReportThemeName === 'highcontrast2017' || dt.baseReportThemeName === 'highcontrast2018') {
          statusTokens = {
            ...tokens,
            'placeholder-fg': dt.c.hcDisabled
          };
        } else {
          statusTokens = {
            ...tokens,
            'placeholder-fg': dt.c.white
          };
        }
        break;
      default:
        statusTokens = {
          ...tokens,
          'placeholder-fg': dt.c.n1
        };
        break;
    }
  } else {
    switch (dt.themeVariant) {
      case 'dark':
        themeTokens = {
          bd: setAlpha(dt.c.white, 0.3),
          bg: dt.c.p_3,
          fg: dt.c.white,
          'font-size': dt.reportTokens.font.size.m,
          'line-height': 'normal',
          'selection-bg': dt.reportTokens.text.selection.bg,
          'selection-fg': dt.reportTokens.text.selection.fg,
          'placeholder-fg': setAlpha(dt.c.white, 0.3),
          'focus-bd': dt.c.p0,
          ...statusTokens
        };
        break;
      case 'high-contrast':
        if (dt.baseReportThemeName === 'highcontrast2017' || dt.baseReportThemeName === 'highcontrast2018') {
          themeTokens = {
            bd: dt.c.white,
            bg: dt.c.black,
            fg: dt.c.white,
            'font-size': dt.reportTokens.font.size.m,
            'line-height': 'normal',
            'selection-bg': dt.reportTokens.text.selection.bg,
            'selection-fg': dt.reportTokens.text.selection.fg,
            'placeholder-fg': dt.c.hcDisabled,
            'focus-bd': dt.c.p0,
            ...statusTokens
          };
        } else {
          themeTokens = {
            bd: dt.c.white,
            bg: dt.c.black,
            fg: dt.c.white,
            'font-size': dt.reportTokens.font.size.m,
            'line-height': 'normal',
            'selection-bg': dt.reportTokens.text.selection.bg,
            'selection-fg': dt.reportTokens.text.selection.fg,
            'placeholder-fg': dt.c.white,
            'focus-bd': dt.c.p3,
            ...statusTokens
          };
        }
        break;
      default:
        themeTokens = {
          bd: dt.c.n1,
          bg: dt.c.white,
          fg: dt.c.n_3,
          'font-size': dt.reportTokens.font.size.m,
          'line-height': 'normal',
          'selection-bg': dt.reportTokens.text.selection.bg,
          'selection-fg': dt.reportTokens.text.selection.fg,
          'placeholder-fg': dt.c.n1,
          'focus-bd': dt.c.p0,
          ...statusTokens
        };
    }
  }
  return {
    ...renderDesignTokens({
      'readonly-bg': dt.reportTokens.stateStandardContent.normal.bg,
      'readonly-bd': 'transparent',
      'readonly-fg': dt.reportTokens.stateStandardContent.normal.fg,
      'noneditable-bg': dt.reportTokens.stateStandard.normal.bg,
      'noneditable-bd': dt.reportTokens.stateStandard.normal.bd,
      'noneditable-fg': dt.reportTokens.stateStandard.normal.fg,
      'disabled-fg': dt.reportTokens.stateStandard.disabled.fg,
      'disabled-bd': dt.reportTokens.stateStandard.disabled.bd,
      'disabled-bg': dt.reportTokens.stateStandard.disabled.bg,
      ...statusTokens,
      ...themeTokens,
      ...opts?.font
    }, 'input'),
    ...renderDesignTokens({
      'noneditable-bd': border
    }, 'input-focus'),
    ...overrideValues
  };
};
//# sourceMappingURL=BaseInput.js.map